import { LogicBlockOrV2 } from 'dashboard/lib/workflows';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { EventName } from 'lib/constants/events';
import { FieldSchemaJson } from 'lib/fields';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';

type TemplateFilters = {
  'transaction-type'?: string[];
};

export type SupportedTriggerEvent =
  | EventName.AccountCreated
  | EventName.AccountStatusUpdated
  // NOTE: AccountTag* events are not generally available. Access to it is gated by the
  // feature flag: global:enable-account-tags-workflow-event-trigger
  | EventName.AccountTagAdded
  | EventName.AccountTagRemoved
  // NOTE: AccountUpdatedFields events are not generally available. Access to it is gated by the
  // feature flag: dashboard:accounts-emit-account-updated-field-events-v2
  | EventName.AccountUpdatedFields
  | EventName.CaseCreated
  | EventName.CaseResolved
  | EventName.CaseReopened
  | EventName.CaseSlaExpired
  | EventName.CaseMessageReceived
  | EventName.CaseMessageSent
  | EventName.CaseStatusUpdated
  | EventName.InquiryCreated
  | EventName.InquiryExpired
  | EventName.InquiryCompleted
  | EventName.InquiryFailed
  | EventName.InquiryMarkedForReview
  | EventName.InquiryApproved
  | EventName.InquiryDeclined
  | EventName.InquiryTemplateVersionCreated
  | EventName.InquiryTemplateVersionPublished
  | EventName.ReportAddressLookupReady
  | EventName.ReportAdverseMediaMatched
  | EventName.ReportAdverseMediaReady
  | EventName.ReportAdverseMediaDismissed
  | EventName.ReportBusinessAdverseMediaReady
  | EventName.ReportBusinessAdverseMediaMatched
  | EventName.ReportBusinessAdverseMediaDismissed
  | EventName.ReportBusinessAssociatedPersonsReady
  | EventName.ReportBusinessAssociatedPersonsMatched
  | EventName.ReportBusinessLookupReady
  | EventName.ReportBusinessNonprofitChanged
  | EventName.ReportBusinessSocialMediaReady
  | EventName.ReportBusinessWatchlistReady
  | EventName.ReportBusinessWatchlistMatched
  | EventName.ReportBusinessWatchlistDismissed
  | EventName.ReportBusinessWebsiteReady
  | EventName.ReportBusinessWebsiteErrored
  | EventName.ReportCustomListReady
  | EventName.ReportCustomListMatched
  | EventName.ReportCustomListDismissed
  | EventName.ReportEmailAddressReady
  | EventName.ReportPhoneNumberReady
  | EventName.ReportPoliticallyExposedPersonMatched
  | EventName.ReportPoliticallyExposedPersonReady
  | EventName.ReportPoliticallyExposedPersonDismissed
  | EventName.ReportScreeningReady
  | EventName.ReportSocialMediaReady
  | EventName.ReportProfileReady
  | EventName.ReportSyntheticReady
  | EventName.ReportWatchlistMatched
  | EventName.ReportWatchlistReady
  | EventName.ReportWatchlistDismissed
  | EventName.SARCreated
  | EventName.SARFiled
  | EventName.TransactionCreated
  | EventName.TransactionStatusUpdated;

export interface TriggerEventAttributes {
  'event-name': SupportedTriggerEvent;
  // Used by Workflow 2.0. And Workflow 1.0 expects {}
  rules: LogicBlockOrV2 | true;
  'template-filters'?: TemplateFilters;
}

export interface TriggerApiAttributes {
  'param-schema': [FieldSchemaJson];
}

export interface TriggerScheduleAttributes {
  'trigger-object-type': TriggerObjectType;
}

type SupportedRecurringTriggerObjectType =
  | ApiModel.Account
  | ApiModel.Case
  | ApiModel.Inquiry
  | ApiModel.Report
  | ApiModel.SAR
  | ApiModel.Transaction;
export interface TriggerRecurringAttributes {
  'trigger-object-type': SupportedRecurringTriggerObjectType;
  rrule: string;
  filter: { name: string; operator: string; value: string | string[]; unit: string }[];
  'start-at': string;
}

export type SupportedComputedTriggerEvent = EventName.TransactionCreated;
export interface TriggerComputedFieldAttributes {
  'field-schema-object-token': string;
  'field-key': string;
  config: SupportedComputedTriggerEvent[];
}

export interface TriggerCaseAttributes {
  'param-schema': TriggerCaseParamSchema[];
}

export interface TriggerAccountAttributes {
  'input-modal-configuration': TriggerAccountInputModalConfiguration[];
}

export const TriggerObjectTypes = [
  ApiModel.Account,
  ApiModel.Case,
  ApiModel.Inquiry,
  ApiModel.Report,
  ApiModel.SAR,
  ApiModel.Transaction,
] as const;

export type TriggerObjectType = (typeof TriggerObjectTypes)[number];

export enum InputSchemaType {
  Dropdown = 'dropdown',
  Input = 'input',
  TextField = 'text_field',
  CheckBox = 'checkbox',
  RadioGroup = 'radio_group',
  Date = 'date',
  Phone = 'phone',
  Address = 'address',
  MaskedInput = 'masked_input',
  Country = 'country',
  USState = 'us_state',
  USStateCode = 'us_state_code',
}

export interface TriggerCaseParamSchema {
  name: string;
  placeholder: string;
  label: string;
  type: InputSchemaType;
  prefill: string;
  required_to_show: {
    name: string;
    values: string[];
  }[];
  optional?: boolean;
  disabled?: boolean;
  options?: {
    label: string;
    value: string;
  }[];
}

export interface TriggerAccountInputModalConfiguration {
  key: string;
  label: string;
  type: InputSchemaType;
  placeholder?: string;
  prefill?: string;
  hidden: string;
  options?: string;
  required: string;
  disabled: string;
}

export interface TriggerRelationships {
  environment: ApiRelationship;
}

export type TriggerEvent = ApiResource<
  TriggerEventAttributes,
  TriggerRelationships,
  ApiModel.TriggerEvent
>;

export type TriggerApi = ApiResource<
  TriggerApiAttributes,
  TriggerRelationships,
  ApiModel.TriggerApi
>;

export type TriggerSchedule = ApiResource<
  TriggerScheduleAttributes,
  TriggerRelationships,
  ApiModel.TriggerSchedule
>;

export type TriggerRecurring = ApiResource<
  TriggerRecurringAttributes,
  TriggerRelationships,
  ApiModel.TriggerRecurring
>;

export type TriggerComputedField = ApiResource<
  TriggerComputedFieldAttributes,
  TriggerRelationships,
  ApiModel.TriggerComputedField
>;

export type TriggerCase = ApiResource<
  TriggerCaseAttributes,
  TriggerRelationships,
  ApiModel.TriggerCase
>;

export type TriggerDirect = ApiResource<
  Record<string, never>,
  TriggerRelationships,
  ApiModel.TriggerDirect
>;

export type TriggerAccount = ApiResource<
  TriggerAccountAttributes,
  TriggerRelationships,
  ApiModel.TriggerAccount
>;

export type Trigger =
  | TriggerEvent
  | TriggerApi
  | TriggerCase
  | TriggerDirect
  | TriggerSchedule
  | TriggerRecurring
  | TriggerComputedField
  | TriggerAccount;

// Need this in workflows params
export const BaseTrigger = 'trigger';
export const BaseTriggerParams = 'trigger_params';

export type TriggerType = ApiModel & `trigger-${string}`;

export const ALL_TRIGGER_TYPES: TriggerType[] = [
  ApiModel.TriggerAccount,
  ApiModel.TriggerApi,
  ApiModel.TriggerCase,
  ApiModel.TriggerEvent,
  ApiModel.TriggerDirect,
  ApiModel.TriggerSchedule,
  ApiModel.TriggerRecurring,
  ApiModel.TriggerComputedField,
];

export const TRIGGER_OBJECT_TYPE_TO_WORKFLOW_FIELD_OBJECT_KEY: Record<
  SupportedRecurringTriggerObjectType,
  WorkflowFieldObjectKey
> = {
  [ApiModel.Account]: WorkflowFieldObjectKey.Account,
  [ApiModel.Case]: WorkflowFieldObjectKey.Case,
  [ApiModel.Inquiry]: WorkflowFieldObjectKey.Inquiry,
  [ApiModel.Report]: WorkflowFieldObjectKey.ReportAddressLookup,
  [ApiModel.SAR]: WorkflowFieldObjectKey.SAR,
  [ApiModel.Transaction]: WorkflowFieldObjectKey.Transaction,
};

export const TRIGGER_EVENT_NAME_TO_OBJECT: Record<SupportedTriggerEvent, WorkflowFieldObjectKey> = {
  [EventName.AccountCreated]: WorkflowFieldObjectKey.Account,
  [EventName.AccountStatusUpdated]: WorkflowFieldObjectKey.Account,
  [EventName.AccountTagAdded]: WorkflowFieldObjectKey.Account,
  [EventName.AccountTagRemoved]: WorkflowFieldObjectKey.Account,
  [EventName.AccountUpdatedFields]: WorkflowFieldObjectKey.Account,

  [EventName.CaseCreated]: WorkflowFieldObjectKey.Case,
  [EventName.CaseResolved]: WorkflowFieldObjectKey.Case,
  [EventName.CaseReopened]: WorkflowFieldObjectKey.Case,
  [EventName.CaseSlaExpired]: WorkflowFieldObjectKey.Case,
  [EventName.CaseMessageReceived]: WorkflowFieldObjectKey.CaseMessage,
  [EventName.CaseMessageSent]: WorkflowFieldObjectKey.CaseMessage,
  [EventName.CaseStatusUpdated]: WorkflowFieldObjectKey.Case,

  [EventName.InquiryCreated]: WorkflowFieldObjectKey.Inquiry,
  [EventName.InquiryExpired]: WorkflowFieldObjectKey.Inquiry,
  [EventName.InquiryCompleted]: WorkflowFieldObjectKey.Inquiry,
  [EventName.InquiryFailed]: WorkflowFieldObjectKey.Inquiry,
  [EventName.InquiryMarkedForReview]: WorkflowFieldObjectKey.Inquiry,
  [EventName.InquiryApproved]: WorkflowFieldObjectKey.Inquiry,
  [EventName.InquiryDeclined]: WorkflowFieldObjectKey.Inquiry,
  [EventName.InquiryTemplateVersionCreated]: WorkflowFieldObjectKey.InquiryTemplateVersion,
  [EventName.InquiryTemplateVersionPublished]: WorkflowFieldObjectKey.InquiryTemplateVersion,

  [EventName.ReportAddressLookupReady]: WorkflowFieldObjectKey.ReportAddressLookup,
  [EventName.ReportAdverseMediaMatched]: WorkflowFieldObjectKey.ReportAdverseMedia,
  [EventName.ReportAdverseMediaReady]: WorkflowFieldObjectKey.ReportAdverseMedia,
  [EventName.ReportAdverseMediaDismissed]: WorkflowFieldObjectKey.ReportAdverseMedia,
  [EventName.ReportBusinessAdverseMediaReady]: WorkflowFieldObjectKey.ReportBusinessAdverseMedia,
  [EventName.ReportBusinessAdverseMediaMatched]: WorkflowFieldObjectKey.ReportBusinessAdverseMedia,
  [EventName.ReportBusinessAdverseMediaDismissed]:
    WorkflowFieldObjectKey.ReportBusinessAdverseMedia,
  [EventName.ReportBusinessAssociatedPersonsReady]:
    WorkflowFieldObjectKey.ReportBusinessAssociatedPersons,
  [EventName.ReportBusinessAssociatedPersonsMatched]:
    WorkflowFieldObjectKey.ReportBusinessAssociatedPersons,
  [EventName.ReportBusinessLookupReady]: WorkflowFieldObjectKey.ReportBusinessLookup,
  [EventName.ReportBusinessNonprofitChanged]: WorkflowFieldObjectKey.ReportBusinessNonprofit,
  [EventName.ReportBusinessSocialMediaReady]: WorkflowFieldObjectKey.ReportBusinessSocialMedia,
  [EventName.ReportBusinessWatchlistReady]: WorkflowFieldObjectKey.ReportBusinessWatchlist,
  [EventName.ReportBusinessWatchlistMatched]: WorkflowFieldObjectKey.ReportBusinessWatchlist,
  [EventName.ReportBusinessWatchlistDismissed]: WorkflowFieldObjectKey.ReportBusinessWatchlist,
  [EventName.ReportBusinessWebsiteReady]: WorkflowFieldObjectKey.ReportBusinessWebsite,
  [EventName.ReportBusinessWebsiteErrored]: WorkflowFieldObjectKey.ReportBusinessWebsite,
  [EventName.ReportCustomListReady]: WorkflowFieldObjectKey.ReportCustomList,
  [EventName.ReportCustomListMatched]: WorkflowFieldObjectKey.ReportCustomList,
  [EventName.ReportCustomListDismissed]: WorkflowFieldObjectKey.ReportCustomList,
  [EventName.ReportEmailAddressReady]: WorkflowFieldObjectKey.ReportEmailAddress,
  [EventName.ReportPhoneNumberReady]: WorkflowFieldObjectKey.ReportPhoneNumber,
  [EventName.ReportPoliticallyExposedPersonMatched]:
    WorkflowFieldObjectKey.ReportPoliticallyExposedPerson,
  [EventName.ReportPoliticallyExposedPersonReady]:
    WorkflowFieldObjectKey.ReportPoliticallyExposedPerson,
  [EventName.ReportPoliticallyExposedPersonDismissed]:
    WorkflowFieldObjectKey.ReportPoliticallyExposedPerson,
  [EventName.ReportSocialMediaReady]: WorkflowFieldObjectKey.ReportSocialMedia,
  [EventName.ReportProfileReady]: WorkflowFieldObjectKey.ReportProfile,
  [EventName.ReportSyntheticReady]: WorkflowFieldObjectKey.ReportSynthetic,
  [EventName.ReportWatchlistMatched]: WorkflowFieldObjectKey.ReportWatchlist,
  [EventName.ReportWatchlistReady]: WorkflowFieldObjectKey.ReportWatchlist,
  [EventName.ReportWatchlistDismissed]: WorkflowFieldObjectKey.ReportWatchlist,
  [EventName.ReportScreeningReady]: WorkflowFieldObjectKey.ReportScreening,

  [EventName.SARCreated]: WorkflowFieldObjectKey.SAR,
  [EventName.SARFiled]: WorkflowFieldObjectKey.SAR,

  [EventName.TransactionCreated]: WorkflowFieldObjectKey.Transaction,
  [EventName.TransactionStatusUpdated]: WorkflowFieldObjectKey.Transaction,
};

export const TRIGGER_EVENTS = Object.keys(TRIGGER_EVENT_NAME_TO_OBJECT) as EventName[];
